import React, { useEffect, useRef, useState } from "react";
import {
  StyledTypography,
  StyledBox,
  StyledLink,
  StyledCheckbox,
  StyledFormControlLabel,
  StyledSwatch,
  StyledButton,
} from "../../../styled-mui";
import { Box, FormControlLabel, FormGroup, useMediaQuery, useTheme } from "@mui/material";
import { TYPE_CARD } from "../../../constants/catalog";
import { ProductAvis } from "../product-avis/ProductAvis";
import { ImageLayout } from "../image";
import { dimensions } from "../../../themes/variables";
import { PriceDisplay } from "../formatted-price-display/formatted-price-display";
import storeUtil from "../../../utils/storeUtil";
import { SvgCheckBoxOutlineBlankIcon } from "../../svg/checkBoxOutlineBlankIcon";
import CheckPdpIcon from "../../svg/checkpdp.svg";
import { StyledProductCard } from "../product-card/styled-productCard";
import { StyledProductImageLabel, StyledProductImageTag } from "../product-image/styled-productImage";
import AsyncCall from "../../../_foundation/gtm/async.service";
import { CLICK } from "../../../_foundation/constants/gtm";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import getDisplayName from "react-display-name";
import kebabCase from "lodash-es/kebabCase";
import { GetCategoriesSelector, GetCurrentCategorySelector } from "../../../redux/selectors/category";
import { useSite } from "../../../_foundation/hooks/useSite";
import { getProductPriceData } from "../../../utils/priceUtil";
import Chip from "@mui/material/Chip";
import ColorSwatches from "../color-swatches/ColorSwatches";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useProductValue } from "../../../_foundation/context/product-context";
import { useTranslation } from "react-i18next";
import { orderIsLoadingSelector } from "../../../redux/selectors/order";

interface ProductCardProps {
  product: any;
  type?: TYPE_CARD;
  defaultProduct?: boolean;
  onSelect?: (action: string, product: any) => void;
  addToCart?: (partNumber: string) => void;
  onAddToCart?: (product: any) => Promise<any> | void; // For showing add to cart button instead of checkbox
  categoryId?: string;
  [key: string]: any;
  showAttribute?: boolean;
  attributes?: any;
  isRecommanded?: any;
  project?: boolean;
  productPosition?: number;
  plpCard?: boolean; // Used to identify if the card is used in PLP
  changeImageOnHover?: boolean;
  isCartConfirmModal?: boolean; // Only show Add to Cart button in CartConfirmModal
  disable?: boolean;
  setDisable?: any;
}

const PdpProductCard: React.FC<ProductCardProps> = ({
  product,
  onSelect,
  attributes,
  type = TYPE_CARD.RECOMMENDATION,
  defaultProduct = false,
  showAttribute = false,
  isRecommanded,
  project,
  productPosition = 0,
  plpCard = false, // Used to identify if the card is used in PLP
  changeImageOnHover = true,
  onAddToCart, // For showing add to cart button instead of checkbox
  isCartConfirmModal = false,
  disable = false,
  setDisable,
}) => {
  const swatches: any[] = [];
  const navigate = useNavigate();
  const productAttributes: any = product?.attributes ? product?.attributes : [];
  const categoriesRedux = useSelector(GetCategoriesSelector);
  const currentCategory = useSelector(GetCurrentCategorySelector);
  const location: any = useLocation();
  const componentName: any = getDisplayName(PdpProductCard);
  const { pathname } = location;
  const pageName: string = pathname === "/" ? "homepage" : pathname.replaceAll("/", "");
  const { mySite } = useSite();
  const itemListId = componentName + "_" + kebabCase(pageName);
  const itemListName = componentName + " " + pageName;
  const badges: any[] = storeUtil.getBadgesFpcProduct(product, mySite);
  const [showFirstImage, setShowFirstImage] = useState<boolean>(true);
  const projectFlow = location.state?.projectFlow;
  const trackingIndex = productPosition + 1;
  // Local loading state for Add to Cart button in modal
  const loading = useSelector(orderIsLoadingSelector);
  const [activePartNumber, setActivePartNumber] = useState<string | null>(null);

  const isSurMesure = product?.UserData?.[0]?.["custom.fields.*"]?.[0]?.catent_field4?.raw === "SUR_MESURE";
  const productOptions = product?.attributes?.filter((p) => p?.usage === "Defining");
  const productOptionsCount = productOptions.reduce((total, attr) => {
    const values = attr?.values?.[0]?.value;
    return total + (Array.isArray(values) ? values.length : 0);
  }, 0);

  const displayTags = storeUtil.getTagsProduct(product) || [];

  const { promoPrice: promotionalOfferPrice, remisedPriceDetails } = getProductPriceData(product);
  const IS_REMISED_PRICE_VALID = remisedPriceDetails?.valid || false;
  const REMISED_OLD_PRICE_END_DATE = remisedPriceDetails?.OldPriceEnd || null;
  const REMISED_OLD_PRICE = remisedPriceDetails?.oldPrice || null;
  const currency = "EUR";
  const IntlPrice = Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
  });
  const avisData = React.useMemo(() => {
    return storeUtil.getAvisProduct(product);
  }, [product]);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isFPC = product?.partNumber?.startsWith("FPC");

  let pdpImages;

  if (isFPC) {
    pdpImages = product?.images?.filter((p) => p?.catentry_id && p?.thumbnailRaw);
  } else {
    pdpImages = product?.images?.filter((p) => !("catentry_id" in p));
  }

  const colors = storeUtil.getColorAttributes(productAttributes);
  const { associatedProductList = [], toggleMerchandisingSelection } = useProductValue();
  const isThisButtonLoading = loading && activePartNumber === product.partNumber;
  colors?.values &&
    colors?.values?.forEach((attributeValue: any, index2: number) => {
      if (
        attributeValue.image1path &&
        Array.isArray(attributeValue.image1path) &&
        attributeValue.image1path.length > 0
      ) {
        attributeValue.image1.forEach((imagePath: any, index3: number) => {
          swatches.push(
            <StyledSwatch
              className="rounded-small"
              style={{
                backgroundImage: `url("${storeUtil.getUrlPicture(imagePath)}")`,
              }}
              key={`${attributeValue.id[index3]}_${index2}_${index3}`}
              title={attributeValue.value[index3]}
              data-testid={`product-card-layout-${attributeValue.id[index3].toLowerCase()}-swatch`}
            />
          );
        });
      } else if (attributeValue.image1path?.length > 0) {
        swatches.push(
          <StyledSwatch
            className="rounded-small"
            style={{
              backgroundImage: `url("${attributeValue.image1path}")`,
            }}
            key={`${attributeValue.id}_${index2}`}
            title={attributeValue.value}
            data-testid={`product-card-layout-${attributeValue.id.toLowerCase()}-swatch`}
          />
        );
      }
    });

  const cardClickHandler = () => {
    AsyncCall.sendClickProduct(CLICK.SELECT_ITEM, {
      topCategoriesList: categoriesRedux,
      product: product,
      label: `card-${type}-${product?.name}`,
      index: trackingIndex,
      item_list_id: itemListId,
      item_list_name: itemListName,
      itemPrice: promotionalOfferPrice || product?.price?.[0]?.value,
    });
    if (isRecommanded?.recommendationId && isRecommanded?.widgetId) {
      AsyncCall.sendClick(CLICK.PRODUCT_RECOMMANDED, {
        sourceID: isRecommanded?.recommendationId,
        widgetID: isRecommanded?.widgetId,
        product_fpc: product.partNumber,
      });
    }
  };

  const associatedProduct = React.useMemo(
    () => associatedProductList.find((p) => p.partNumber === product.partNumber),
    [associatedProductList, product.partNumber]
  );

  const isAssociatedSelected = Boolean(associatedProduct?.isSelected);

  return (
    <StyledProductCard>
      <StyledBox
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? 1.5 : 3,
          p: "14px",
          border: isAssociatedSelected ? "1px solid #2F5E73" : "1px solid #E0E0E0",
          borderRadius: "8px",
          cursor: "pointer",
        }}
        onClick={() => {
          navigate(
            !mySite.storeCfg.userData.SEO_FE_DISABLE ||
              (mySite.storeCfg.userData.SEO_FE_DISABLE && mySite.storeCfg.userData.SEO_FE_DISABLE !== "true")
              ? `/produits${product?.seo?.href}`
              : product?.seo?.href
          );
          cardClickHandler();
        }}>
        {/* TOP/LEFT : IMAGE + NAME + REVIEWS */}
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? 2 : 3,
            flex: 1,
            width: "100%",
          }}>
          {/* IMAGE */}
          <Box sx={{ width: isMobile ? 60 : 97, flexShrink: 0 }}>
            <ImageLayout
              src={storeUtil.getUrlPicture(product?.thumbnailRaw)}
              alt={product.shortDescription}
              width={isMobile ? "60px" : "97px"}
              height={isMobile ? "60px" : "70px"}
              styles={{ objectFit: "contain" }}
            />
          </Box>

          {/* NAME + REVIEWS */}
          <Box sx={{ flex: 1 }}>
            <StyledTypography variant="subtitle2" sx={{ mb: 0.5, fontSize: "15px" }}>
              {product.name}
            </StyledTypography>

            <ProductAvis yellowStarRatings avis={avisData} />
          </Box>
        </Box>

        {/* BOTTOM/RIGHT : PRICE + CHECKBOX */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isMobile ? "space-between" : "flex-end",
            gap: 2,
            width: isMobile ? "100%" : "auto",
          }}
          onClick={(e) => e.stopPropagation()}>
          {product?.["promoPriceData.numeric"] && (
            <PriceDisplay product={product} variant="body1" isPLP={plpCard} isBundle={true} />
          )}

          {isCartConfirmModal && !defaultProduct && onAddToCart ? (
            // Show Add to Cart button only in CartConfirmModal (popin)
            <StyledButton
              variant="contained"
              color="primary"
              size="small"
              loading={isThisButtonLoading}
              disabled={disable || isThisButtonLoading}
              onClick={async () => {
                if (disable) return;
                setActivePartNumber(product.partNumber);
                await Promise.resolve(onAddToCart(product));
                setDisable(true);
              }}
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                padding: "8px 16px",
                fontWeight: 600,
                backgroundColor: "#C62828",
              }}>
              {t("productDetail.AddToCart")}
            </StyledButton>
          ) : !defaultProduct ? (
            // Show checkbox
            <StyledCheckbox
              disableRipple
              checked={isAssociatedSelected}
              onChange={(e) => toggleMerchandisingSelection(product.partNumber, e.target.checked)}
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
              icon={
                <Box
                  sx={{
                    width: "44px",
                    height: "44px",
                    // borderRadius: "6px",
                    border: "1px solid #E0E0E0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    fontWeight: 700,
                  }}>
                  +
                </Box>
              }
              checkedIcon={
                <Box
                  sx={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "6px",
                    backgroundColor: "#2C5F7C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <ImageLayout src={CheckPdpIcon} />
                </Box>
              }
            />
          ) : null}
        </Box>
      </StyledBox>
    </StyledProductCard>
  );
};

export default PdpProductCard;
